import React from "react";
import { useTranslation } from "react-i18next";
import {
    Gauge,
    ListChecks,
    AlertOctagon,
    ShieldAlert,
    KeyRound,
    Wrench,
    LineChart,
    MessagesSquare,
    ClipboardCheck,
} from "lucide-react";

export default function Features() {
    const { t } = useTranslation();

    const checks = [
        { n: "01", icon: KeyRound, title: t("landing.features.check5Title"), sub: t("landing.features.check5Sub") },
        { n: "02", icon: ListChecks, title: t("landing.features.check2Title"), sub: t("landing.features.check2Sub") },
        { n: "03", icon: AlertOctagon, title: t("landing.features.check3Title"), sub: t("landing.features.check3Sub") },
        { n: "04", icon: ShieldAlert, title: t("landing.features.check4Title"), sub: t("landing.features.check4Sub") },
        { n: "05", icon: Gauge, title: t("landing.features.check1Title"), sub: t("landing.features.check1Sub") },
        { n: "06", icon: Wrench, title: t("landing.features.check6Title"), sub: t("landing.features.check6Sub") },
        { n: "07", icon: LineChart, title: t("landing.features.check7Title"), sub: t("landing.features.check7Sub") },
        { n: "08", icon: MessagesSquare, title: t("landing.features.check8Title"), sub: t("landing.features.check8Sub") },
        { n: "09", icon: ClipboardCheck, title: t("landing.features.check9Title"), sub: t("landing.features.check9Sub") },
    ];

    return (
        <section
            id="features"
            className="scroll-mt-20 border-b-2 border-black bg-white"
            data-testid="features-section"
        >
            {/* Header */}
            <div className="border-b-2 border-black">
                <div className="mx-auto max-w-[1400px] grid grid-cols-12">
                    <div className="col-span-12 md:col-span-7 border-r-0 md:border-r-2 border-b-2 md:border-b-0 border-black p-8 md:p-12">
                        <span className="font-mono text-xs tracking-[0.25em] uppercase border-2 border-black bg-white px-3 py-1 inline-block">
                            {t("landing.features.eyebrow")}
                        </span>
                        <h2 className="mt-6 font-display text-4xl md:text-6xl uppercase leading-[0.95] tracking-tighter">
                            {t("landing.features.headlineLine1")}
                            <br />
                            {t("landing.features.headlineLine2")}
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 p-8 md:p-12 flex items-end">
                        <p className="text-base md:text-lg leading-relaxed text-zinc-700">
                            {t("landing.features.description")}
                        </p>
                    </div>
                </div>
            </div>

            {/* One tight 3×3 grid — visually grouped as one block */}
            <div className="mx-auto max-w-[1400px] p-6 md:p-10">
                <div
                    className="border-2 border-black shadow-brutal-lg"
                    data-testid="checks-grid"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                        {checks.map((c, i) => {
                            const Icon = c.icon;
                            const isCenter = i === 4;
                            return (
                                <div
                                    key={c.n}
                                    data-testid={`check-${c.n}`}
                                    className={`relative p-5 md:p-7 ${
                                        isCenter
                                            ? "bg-[#FFCC00] text-black"
                                            : "bg-white text-black"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <Icon size={22} strokeWidth={2} />
                                        <span className="font-mono text-[10px] tracking-[0.25em] uppercase opacity-60">
                                            {c.n}
                                        </span>
                                    </div>
                                    <h3 className="mt-6 md:mt-10 font-display text-base sm:text-lg md:text-2xl uppercase leading-tight tracking-tight">
                                        {c.title}
                                    </h3>
                                    <p className="mt-1 font-mono text-[10px] sm:text-xs uppercase tracking-[0.15em] opacity-70">
                                        {c.sub}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Bottom inline CTA strip */}
                <div className="mt-8 md:mt-10 border-2 border-black bg-white p-5 md:p-6 flex flex-wrap items-center justify-between gap-4">
                    <div className="font-mono text-xs sm:text-sm uppercase tracking-[0.2em]">
                        {t("landing.features.ctaStrip")}
                    </div>
                    <a
                        href="#pricing"
                        data-testid="features-cta-pricing"
                        className="btn-brutal border-2 border-black bg-black text-white px-7 py-3 font-mono text-xs uppercase tracking-[0.25em] shadow-brutal-sm"
                    >
                        {t("landing.features.ctaPricing")}
                    </a>
                </div>
            </div>
        </section>
    );
}
